import { darken, saturate } from 'polished';
import styled, { css } from 'styled-components';

import { type Theme } from '@/utils/theme/themes';

export function gridColorSelector(theme: Theme, shade: 'major' | 'minor') {
  return shade === 'major' ? theme.palette.fg800 : theme.palette.fg500;
}

export const ContentMarginLine = styled.div<{
  'data-bottom'?: boolean;
  'data-minor'?: boolean;
  'data-right'?: boolean;
  'data-top'?: boolean;
}>`
  --border-color: ${({ theme }) => gridColorSelector(theme, 'major')};

  grid-column: 1;
  border-right: 1px solid var(--border-color);
  align-self: stretch;

  &[data-right] {
    border-right: none;
    border-left: 1px solid var(--border-color);
    grid-column: -2 / span 1;
  }

  &[data-minor] {
    --border-color: ${({ theme }) => gridColorSelector(theme, 'minor')};
  }

  &[data-top] {
    border-top: 1px solid ${({ theme }) => gridColorSelector(theme, 'minor')};
  }

  &[data-bottom] {
    border-bottom: 1px solid ${({ theme }) => gridColorSelector(theme, 'minor')};
  }

  &[data-v1-gridlines] {
    background-image: linear-gradient(
      to bottom,
      transparent calc(100% - 1px),
      ${({ theme }) => gridColorSelector(theme, 'minor')} calc(100% - 1px)
    );
    background-size: 100% 10rem;
  }
`;

export const GridRule = styled.div<{
  $column?: number;
}>`
  align-self: stretch;
  justify-self: flex-end;
  width: 1px;
  background-color: ${({ theme }) => gridColorSelector(theme, 'minor')};
  grid-column: ${({ $column }) => $column || 1} !important;
  transform: translateX(50%);
  grid-row: 1 / -1;
`;

export const ContentMarginLAccents = styled(ContentMarginLine)`
  --accent-color: ${({ theme }) => theme.palette.fg1200};
  /* grid-row: 1 / -1;
  grid-column: 1 / -1; */
  position: absolute;
  inset: 0;
  display: none;

  position: relative;
  border-right-color: transparent;

  &[data-right] {
    border-left-color: transparent;
  }

  &::before {
    z-index: 1;
    content: '';
    position: absolute;
    height: 4px;
    width: 4px;
    right: 0;
    top: 0;
    border-bottom: 1px solid var(--accent-color);
    border-right: 1px solid var(--accent-color);
    transform: translate(1px, -100%);
  }

  &::after {
    z-index: 1;
    content: '';
    position: absolute;
    height: 4px;
    width: 4px;
    right: 0;
    bottom: 0;
    border-top: 1px solid var(--accent-color);
    border-right: 1px solid var(--accent-color);
    transform: translate(1px, 100%);
  }

  &[data-right]::after {
    right: auto;
    left: 0;
    border-right: 0;
    border-left: 1px solid var(--accent-color);
    transform: translate(-1px, 100%);
  }

  &[data-right]::before {
    right: auto;
    left: 0;
    border-right: 0;
    border-left: 1px solid var(--accent-color);
    transform: translate(-1px, -100%);
  }
`;

export function backgroundImageCheckered(theme: Theme) {
  const fill = encodeURIComponent(theme.palette.fg600);
  const size = 4;
  const rect1 = `<rect width="${size * 0.5}" height="${size * 0.5}" fill="${fill}" />`;
  const rect2 = `<rect x="${size * 0.5}" y="${size * 0.5}" width="${size * 0.5}" height="${size * 0.5}" fill="${fill}"/>`;
  return `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">${rect1}${rect2}</svg>')`;
}

export const CheckeredMargin = styled.div`
  position: relative;
  background-image: ${({ theme }) => backgroundImageCheckered(theme)};
  grid-column: 1 / span 1;
  border-right: 1px solid ${({ theme }) => gridColorSelector(theme, 'major')};

  &[data-right] {
    border-left: 1px solid ${({ theme }) => gridColorSelector(theme, 'major')};
    border-right: unset;
    grid-column: -2 / span 1;
  }
`;

export function backgroundImageHatched(theme: Theme) {
  return `repeating-linear-gradient(
    45deg,
    transparent 0px,
    transparent calc(var(--hatch-size, 6px) - 1px),
    var(--hatch-color, ${darken(0.25, saturate(0.08, theme.palette.fgA400))}) calc(var(--hatch-size, 6px) - 1px),
    var(--hatch-color, ${darken(0.25, saturate(0.08, theme.palette.fgA400))}) var(--hatch-size, 6px)
  )`;
}

export function cssDotGrid(theme: Theme) {
  return css`
    background-image: radial-gradient(
      ${theme.palette.fgA700},
      ${theme.palette.fgA700} 1px,
      transparent 1px,
      transparent
    );
    background-size: 6px 6px;
  `;
}
